import { AbstractControlOptions, ValidatorFn } from '@angular/forms';
import { FormGroupTyped } from 'src/types/typed-forms';

export interface FormMappingModelPrototype {
  new (): FormMappingModel;

  formGroup<T>(
    o?: object,
    validatorOrOpts?: ValidatorFn | ValidatorFn[] | AbstractControlOptions | null
  ): FormGroupTyped<T>;

  from<T>(o: object): T;
  initialisedFormGroup<T, U>(value: T): FormGroupTyped<U>;
  merge<T>(o: T, form): T;
}

/**
 * This model represents the values of a reactive form and provides helper functions.
 *
 * The helper functions use static methods that are added to the MappingModels via the @FormMapping decorator
 */
export class FormMappingModel {
  /**
   * Create a FormGroup object for this FormMappingModel. It's possible to overwrite properties that are then passed to the FormBuilder
   * @param o Map of properties that are to be overwritten
   * @param validatorOrOpts Validator for the form group
   */
  static formGroup(
    o?: Object,
    validatorOrOpts?: ValidatorFn | ValidatorFn[] | AbstractControlOptions | null
  ): FormGroupTyped<any> {
    // Get the prototype and call the static function formGroup (set in the @FormMapping decorator)
    return (this.prototype as FormMappingModelPrototype).formGroup(o, validatorOrOpts);
  }

  /**
   * Create a value object for this FormMappingModel based on the given Model.
   * @param o Model that will be used as the source
   */
  static from(o): any {
    // Get the prototype and call the static function from (set in the @FormMapping decorator)
    return (this.prototype as any).from(o);
  }

  /**
   * Create a FormGroup that's value will already be patched with the given values (which will be mapped via .from(value)).
   * @param value
   */
  static initialisedFormGroup<T = any>(value: T): FormGroupTyped<any> {
    const formGroup = this.formGroup();

    formGroup.patchValue(this.from(value));

    return formGroup;
  }

  /**
   * Merges the source object with the current value of the given form. Used to get the value that's then sent to the backend
   * @param o Source object
   * @param form Form from which the current value will be merged with the source object
   */
  static merge<T, U>(o: T, form: U): T {
    // Get the prototype and call the static function merge (set in the @FormMapping decorator)
    return (this.prototype as any).merge(o, form);
  }
}
